import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { Button } from '@/components/ui/button';
import { Add } from '@mui/icons-material';
import { Ellipsis } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ButtonLoading } from '@/components/ui/button';
import { ContextFilesDataTable } from './table/data-table';
import { contextFilesColumns, type ContextFile } from './table/columns';
import { DocumentsTableLoading } from '../documents/table/loading';
import { UploadDialog, type UploadFormValues } from '../documents/upload-dialog';
import { KnowledgeDetailsDialog } from './knowledge-details-dialog';
import {
  QUERY_LIST_UPLOADED_FILES,
  QUERY_GET_KNOWLEDGE_BY_FILE,
  QUERY_GET_AVAILABLE_CITIES,
} from '@/graphql/queries/chatKnowledge';
import {
  MUTATION_ADD_AI_CONTEXT,
  MUTATION_REMOVE_AI_CONTEXT,
} from '@/graphql/mutation/chatKnowledge';
import type {
  UploadedFile,
  AvailableCity,
  AIContext,
  FileKnowledge,
} from '@/graphql/types/chatKnowledge';

export const ContextFilesSection = () => {
  const apolloClient = useApolloClient();
  const { alert, showAlert, handleCloseAlert } = useAlert();

  const [loadingData, setLoadingData] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [loadingKnowledge, setLoadingKnowledge] = useState<Record<string, boolean>>({});
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [knowledgeByFileId, setKnowledgeByFileId] = useState<Record<string, FileKnowledge[]>>({});
  const [availableCities, setAvailableCities] = useState<AvailableCity[]>([]);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [openKnowledgeDetailsDialog, setOpenKnowledgeDetailsDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<UploadedFile | null>(null);
  const [selectedKnowledge, setSelectedKnowledge] = useState<FileKnowledge | null>(null);
  const [expandedFileId, setExpandedFileId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const normalizedData = useMemo(() => {
    return files.map((file) => {
      const fileKnowledge = knowledgeByFileId[file.id] || [];

      return {
        id: file.id,
        filename: file.filename,
        original_name: file.original_name,
        category: file.category,
        municipality_code: file.municipality_code,
        file_size: file.file_size,
        created_at: file.created_at,
        knowledge_count: fileKnowledge.length,
        actions: (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <Ellipsis />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent side="bottom" align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItem(file);
                  setOpenRemoveDialog(true);
                }}
                className="text-red-600"
              >
                <span>Remover</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      } as ContextFile;
    });
  }, [files, knowledgeByFileId]);

  const getContextFiles = useCallback(async (searchTerm?: string) => {
    try {
      setLoadingData(true);

      // Fetch files
      const filesResult = await apolloClient.query<{ listUploadedFiles: UploadedFile[] }>({
        query: QUERY_LIST_UPLOADED_FILES,
        variables: {
          search: searchTerm && searchTerm.trim() !== '' ? searchTerm.trim() : undefined,
          limit: 100,
          offset: 0,
        },
        fetchPolicy: 'network-only',
      });
      setFiles(filesResult.data.listUploadedFiles);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingData(false);
    }
  }, [apolloClient, showAlert]);

  const getFileKnowledge = useCallback(async (fileId: string) => {
    // Don't fetch if already loaded
    if (knowledgeByFileId[fileId]) {
      return;
    }

    try {
      setLoadingKnowledge((prev) => ({ ...prev, [fileId]: true }));
      const { data } = await apolloClient.query<{ getKnowledgeByFile: FileKnowledge[] }>({
        query: QUERY_GET_KNOWLEDGE_BY_FILE,
        variables: {
          uploaded_file_id: fileId,
          limit: 100,
          offset: 0,
        },
        fetchPolicy: 'network-only',
      });
      setKnowledgeByFileId((prev) => ({
        ...prev,
        [fileId]: data.getKnowledgeByFile,
      }));
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingKnowledge((prev) => ({ ...prev, [fileId]: false }));
    }
  }, [apolloClient, showAlert, knowledgeByFileId]);

  const getContextFilesRef = useRef(getContextFiles);
  useEffect(() => {
    getContextFilesRef.current = getContextFiles;
  }, [getContextFiles]);

  const getAvailableCities = async () => {
    try {
      const { data } = await apolloClient.query<{ getAvailableCities: AvailableCity[] }>({
        query: QUERY_GET_AVAILABLE_CITIES,
        variables: {
          limit: 1000,
          offset: 0,
        },
      });
      setAvailableCities(data.getAvailableCities);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    }
  };

  const handleUpload = async (values: UploadFormValues) => {
    if (!values.file) return;

    try {
      setLoadingForm(true);
      await apolloClient.mutate<{ addAIContext: AIContext }>({
        mutation: MUTATION_ADD_AI_CONTEXT,
        variables: {
          file: values.file,
          category: values.category,
          municipality_code: values.municipality_code || undefined,
        },
      });

      setOpenUploadDialog(false);
      showAlert('success', 'Arquivo enviado com sucesso');
      await getContextFiles(search);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingForm(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedItem) return;

    try {
      setLoadingForm(true);
      await apolloClient.mutate({
        mutation: MUTATION_REMOVE_AI_CONTEXT,
        variables: {
          uploaded_file_id: selectedItem.id,
        },
      });

      setOpenRemoveDialog(false);
      showAlert('success', 'Arquivo removido com sucesso');
      setSelectedItem(null);
      await getContextFiles(search);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingForm(false);
    }
  };

  const handleRowClick = useCallback((file: ContextFile) => {
    const newExpandedId = expandedFileId === file.id ? null : file.id;
    setExpandedFileId(newExpandedId);

    // Fetch knowledge when expanding
    if (newExpandedId && !knowledgeByFileId[newExpandedId]) {
      getFileKnowledge(newExpandedId);
    }
  }, [expandedFileId, knowledgeByFileId, getFileKnowledge]);

  const handleKnowledgeRowClick = useCallback((knowledge: FileKnowledge) => {
    setSelectedKnowledge(knowledge);
    setOpenKnowledgeDetailsDialog(true);
  }, []);

  // Initial load
  useEffect(() => {
    getContextFiles();
    getAvailableCities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      getContextFilesRef.current(search);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [search]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={() => setOpenUploadDialog(true)}
        >
          <Add />
          Upload Arquivo
        </Button>
      </div>

      {loadingData ? (
        <DocumentsTableLoading />
      ) : (
        <ContextFilesDataTable
          columns={contextFilesColumns}
          data={normalizedData}
          onSearchChange={setSearch}
          onRowClick={handleRowClick}
          expandedFileId={expandedFileId}
          knowledgeByFileId={knowledgeByFileId}
          loadingKnowledge={loadingKnowledge}
          onKnowledgeRowClick={handleKnowledgeRowClick}
        />
      )}

      <UploadDialog
        open={openUploadDialog}
        onOpenChange={setOpenUploadDialog}
        onSubmit={handleUpload}
        loading={loadingForm}
        availableCities={availableCities}
      />

      <Dialog open={openRemoveDialog} onOpenChange={setOpenRemoveDialog}>
        <DialogContent className="gap-6">
          <DialogHeader className="w-fit">
            <DialogTitle>Remover Arquivo</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja remover o arquivo:{' '}
            <span className="font-semibold">
              {selectedItem?.original_name || 'Este arquivo'}
            </span>
            ?
          </p>
          <DialogFooter className="flex-col">
            <Button
              variant="outline"
              onClick={() => {
                setOpenRemoveDialog(false);
                setSelectedItem(null);
              }}
            >
              Cancelar
            </Button>
            <ButtonLoading
              variant="default"
              label="Remover arquivo"
              loading={loadingForm}
              onClick={handleRemove}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <KnowledgeDetailsDialog
        open={openKnowledgeDetailsDialog}
        onOpenChange={(open) => {
          setOpenKnowledgeDetailsDialog(open);
          if (!open) {
            setSelectedKnowledge(null);
          }
        }}
        knowledge={selectedKnowledge}
      />

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </div>
  );
};
